import ClientBookingRequests from './pages/ClientBookingRequests';
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmDialog from "./components/ConfirmDialog";

// Components
import Login from "./components/Login";
import LoginVerifyOtp from "./components/LoginVerifyOtp";
import ForgotPassword from "./components/ForgotPassword";
import AutoLogin from "./pages/AutoLogin";
import DashBoard from "./pages/DashBoard";
import PersonalInformation from "./pages/profile/PersonalInformation";
import AdminProfileDashboard from "./pages/AdminProfileDashboard";
import NotificationsPage from "./pages/profile/NotificationsPage";
import SettingsHub from "./pages/SettingsHub";
import HelpSupport from "./pages/HelpSupport";
import "./styles/AdminSupportManagement.css";
import SupportManagementLayout from "./pages/support-management/SupportManagementLayout";
import SupportDashboardPage from "./pages/support-management/SupportDashboardPage";
import SupportTicketManagement from "./pages/support-management/SupportTicketManagement";
import SupportTicketResolution from "./pages/support-management/SupportTicketResolution";
import SupportReportedIssues from "./pages/support-management/SupportReportedIssues";
import SupportReportedIssueDetail from "./pages/support-management/SupportReportedIssueDetail";
import SupportFaqManagement from "./pages/support-management/SupportFaqManagement";
import SupportLiveChatRequests from "./pages/support-management/SupportLiveChatRequests";
import SupportKnowledgeBase from "./pages/support-management/SupportKnowledgeBase";
import SupportAnalytics from "./pages/support-management/SupportAnalytics";
import ActivityOverview from "./pages/ActivityOverview";
import DeviceSessions from "./pages/DeviceSessions";
import UserActivityHub from "./pages/UserActivity/UserActivityHub";
import RoleProfiles from "./pages/UserActivity/RoleProfiles";
import BranchManagement from "./pages/BranchManagement";
import DashboardPreferences from "./pages/DashboardPreferences";
import DataUsagePolicy from "./pages/DataUsagePolicy";
import TermsConditions from "./pages/TermsConditions";
import FAQ from "./pages/FAQ";
import ReportIssue from "./pages/ReportIssue";
import CreateSupportTicket from "./pages/CreateSupportTicket";
import {
  ADMIN_PORTAL_ACCESS_DENIED,
  canAccessAdminPortal,
  isSuperAdminUser,
  readStoredUserRoles,
} from "./utils/adminProfileRouting";
import BillingOnlyShell from "./pages/BillingOnlyShell";
import { graphqlRequest } from "./utils/graphqlRequest";
import {
  BILLING_ACCESS_EVENT,
  announceBillingAccess,
} from "./utils/subscriptionAccess";

/** Asks the backend whether this company's subscription is live (Super Admins only). */
const checkBillingAccess = () =>
  graphqlRequest(`query { getMyBillingAccess }`)
    .then((res) =>
      announceBillingAccess(res?.getMyBillingAccess?.access === "ended" ? "ended" : "full"),
    )
    .catch(() => {}); // blocked/removed errors are announced by graphqlRequest itself
// import DashboardSummary from "./pages/DashboardSummary";

function clearAdminSession() {
  Cookies.remove("accessToken");
  Cookies.remove("companyId");
  Cookies.remove("companyName");
  Cookies.remove("userName");
  Cookies.remove("userId");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userPermissions");
}

/** Read cookie/roles synchronously so refresh does not flash to /login and drop ?page=. */
function readSessionUser() {
  const token = Cookies.get("accessToken");
  if (!token) return null;
  if (!canAccessAdminPortal(readStoredUserRoles())) return null;
  return { accessToken: token };
}

/** Old `/admin/...` URLs → same paths without the `/admin` prefix. */
function LegacyAdminRedirect() {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/admin\/?/, "") || "";
  const to = rest ? `/${rest}` : "/";
  return <Navigate to={`${to}${search}`} replace />;
}

/** Keep current URL when kicking unauthenticated users to login (login itself still goes to /). */
function RequireAuth({ user, children }) {
  const location = useLocation();
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  return children;
}

function App() {
  const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);
  // Hydrate from cookie on first render — avoids / → /login → / wipe of ?page= on refresh.
  const [user, setUser] = useState(() => readSessionUser());

  // 🔐 Re-check session + kick non-admins out of the admin portal
  useEffect(() => {
    const token = Cookies.get("accessToken");
    if (!token) {
      setUser(null);
      return;
    }
    if (!canAccessAdminPortal(readStoredUserRoles())) {
      clearAdminSession();
      setUser(null);
      setAccessDeniedMessage(ADMIN_PORTAL_ACCESS_DENIED);
      return;
    }
    setUser({ accessToken: token });
  }, []);

  const handleLoginSuccess = (userData) => {
    setAccessDeniedMessage(null);
    setUser(userData);
  };

  // Subscription state: "ended" → billing-only screen; "blocked" → logged out with the reason.
  const [billingOnly, setBillingOnly] = useState(false);
  useEffect(() => {
    const onAccess = (e) => {
      const { access, message } = e.detail || {};
      if (access === "blocked") {
        clearAdminSession();
        setUser(null);
        setBillingOnly(false);
        setAccessDeniedMessage(message);
      } else {
        setBillingOnly(access === "ended");
      }
    };
    window.addEventListener(BILLING_ACCESS_EVENT, onAccess);
    return () => window.removeEventListener(BILLING_ACCESS_EVENT, onAccess);
  }, []);

  useEffect(() => {
    if (user && isSuperAdminUser(readStoredUserRoles())) checkBillingAccess();
    else setBillingOnly(false);
  }, [user]);

  // Paying an invoice happens in a Stripe tab — re-check when the user comes back.
  useEffect(() => {
    if (!billingOnly) return undefined;
    window.addEventListener("focus", checkBillingAccess);
    return () => window.removeEventListener("focus", checkBillingAccess);
  }, [billingOnly]);

  const dashboardShell = (
    <RequireAuth user={user}>
      {billingOnly ? <BillingOnlyShell /> : <DashBoard user={user} />}
    </RequireAuth>
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        style={{ zIndex: 100000 }}
        toastStyle={{ zIndex: 100000 }}
      />
      <ConfirmDialog />

      <Routes>
        {/* Auto-login: website redirects here after payment with ?token=JWT */}
        <Route path="/auth" element={<AutoLogin />} />
        <Route path="/auto-login" element={<AutoLogin />} />

        {/* Login */}
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/" replace />
            ) : (
              <Login accessDeniedMessage={accessDeniedMessage} />
            )
          }
        />
        <Route
          path="/login/verify-code"
          element={
            user ? (
              <Navigate to="/" replace />
            ) : (
              <LoginVerifyOtp onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route
          path="/forgot-password"
          element={
            user ? <Navigate to="/" replace /> : <ForgotPassword />
          }
        />
        <Route
          path="/forget-password"
          element={
            user ? <Navigate to="/" replace /> : <ForgotPassword />
          }
        />

        {/* Public legal pages – accessible without authentication */}
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/data-usage-policy" element={<DataUsagePolicy />} />

        {/* Legacy `/admin` URLs (bookmark / old login redirects) */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="/admin/*" element={<LegacyAdminRedirect />} />

        {/* Dashboard shell at `/` */}
        <Route path="/" element={dashboardShell}>
          <Route index element={null} />
          <Route path="profile-dashboard" element={<AdminProfileDashboard />} />
          <Route path="profile-dashboard/:userId" element={<AdminProfileDashboard />} />
          <Route path="profile" element={<PersonalInformation />} />
          <Route path="booking-requests" element={<ClientBookingRequests />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="account-settings" element={<SettingsHub />} />
          <Route path="help-support" element={<HelpSupport />} />
          <Route path="user-activity" element={<UserActivityHub />} />
          <Route
            path="active-sessions"
            element={
              <Navigate
                to={{ pathname: '/user-activity', search: '?tab=sessions' }}
                replace
              />
            }
          />
          <Route
            path="login-history"
            element={
              <Navigate
                to={{ pathname: '/user-activity', search: '?tab=login-history' }}
                replace
              />
            }
          />
          <Route path="role-profiles" element={<RoleProfiles />} />
          <Route path="branch-management" element={<BranchManagement />} />
          <Route path="support-management/*" element={<SupportManagementLayout />}>
            <Route
              index
              element={<Navigate to="tickets" replace />}
            />
            <Route path="dashboard" element={<SupportDashboardPage />} />
            <Route path="tickets/:ticketId/resolve" element={<SupportTicketResolution />} />
            <Route path="tickets" element={<SupportTicketManagement />} />
            <Route path="reported-issues/:issueId" element={<SupportReportedIssueDetail />} />
            <Route path="reported-issues" element={<SupportReportedIssues />} />
            <Route path="faq-management" element={<SupportFaqManagement />} />
            <Route path="live-chat" element={<SupportLiveChatRequests />} />
            <Route path="knowledge-base" element={<SupportKnowledgeBase />} />
            <Route path="analytics" element={<SupportAnalytics />} />
          </Route>
          {/* <Route path="activity-overview" element={<ActivityOverview />} /> */}
          <Route path="device-sessions" element={<DeviceSessions />} />
          <Route path="dashboard-preferences" element={<DashboardPreferences />} />
          <Route path="data-usage-policy" element={<DataUsagePolicy />} />
          <Route path="terms-conditions" element={<TermsConditions />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="report-issue" element={<ReportIssue />} />
          <Route path="new-ticket" element={<CreateSupportTicket />} />
        </Route>

        {/* fallback */}
        <Route
          path="*"
          element={<Navigate to={user ? "/" : "/login"} replace />}
        />
      </Routes>
    </>
  );
}

export default App;
